import type { BaseRange } from 'slate';

import { Editor, Transforms } from 'slate';

import { MenuHandle } from '@ui/MarkdownEditor/editor/blocks';
import { MenuActionTypes } from '@ui/MarkdownEditor/enums';

import { openCustomBlocksMenu } from '../blocks/ReusableContent/operations';

import { insertCustomBlock } from './utils';

const afterAngleBracket = (editor: Editor) => {
  if (editor.selection?.anchor.offset === 0) return false;

  const anchor = editor.selection!.anchor;
  const focus = { path: anchor.path, offset: anchor.offset - 1 };
  const char = Editor.string(editor, { anchor, focus });

  return char === '<';
};

const insertOrCleanUp = (editor: Editor, { closeTag = false }) => {
  const [{ filtered, selected, rangeRef }, dispatch] = editor.customBlocksMenu;

  if (!rangeRef) {
    // eslint-disable-next-line no-console
    console.warn('Reusable content menu appears closed?');
    return;
  }

  const block = filtered[selected];

  if (closeTag) {
    Transforms.insertText(editor, '>');
  } else if (block && 'tag' in block) {
    insertCustomBlock(editor, block, { at: rangeRef.current });
  } else if (block && 'term' in block) {
    // inserts a glossary term as plain mdx text
    Transforms.insertText(editor, `<Glossary>${block.term}</Glossary>`, { at: rangeRef.current as BaseRange });
  } else {
    Transforms.insertText(editor, ' ');
  }

  dispatch({ type: MenuActionTypes.close });

  const entry = Editor.above(editor, { at: rangeRef.current as BaseRange, match: MenuHandle.is });
  if (entry) Transforms.unwrapNodes(editor, { at: entry[1] });
};

const onKeyDown = (event: KeyboardEvent, editor: Editor) => {
  const { disallowCustomBlocks, useMDX } = editor.props;
  if (!useMDX && disallowCustomBlocks) return;

  const [{ open, rangeRef }, dispatch] = editor.customBlocksMenu;

  if (open) {
    if (!rangeRef) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
      case '>': {
        event.preventDefault();
        event.stopPropagation();

        insertOrCleanUp(editor, { closeTag: event.key === '>' });

        break;
      }
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        event.stopPropagation();

        dispatch({ type: event.key === 'ArrowDown' ? MenuActionTypes.down : MenuActionTypes.up });

        break;
      }
      case 'Escape':
      case '<': {
        // don't stop propagation if the keypress is '<'
        // so the event still triggers the variable menu
        if (event.key !== '<') {
          event.preventDefault();
          event.stopPropagation();
        }

        const entry = Editor.above(editor, { at: rangeRef.current as BaseRange, match: MenuHandle.is });
        if (entry) Transforms.unwrapNodes(editor, { at: entry[1] });

        dispatch({ type: MenuActionTypes.close });

        break;
      }
      default:
        break;
    }
  }

  if (event.key === '<' && !afterAngleBracket(editor)) {
    event.preventDefault();
    event.stopPropagation();

    openCustomBlocksMenu(editor, true);
  }
};

export default onKeyDown;
